import { GetStaticPropsContext } from 'next';
import Image from 'next/image';

import { Box, Divider, Grid, GridItem, Heading, SimpleGrid } from '@chakra-ui/react';

import { Card } from '~/components/card';
import { ContentContainer } from '~/components/container';
import { PageCategory } from '~/features/analytics/constants';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { PersonalizedDiscountForm } from '~/features/personalized-discount/components/personalized-discount-form';
import { PersonalizedDiscountHero } from '~/features/personalized-discount/components/personalized-discount-hero';
import { UdacityBenefitsSection } from '~/features/personalized-discount/components/udacity-benefits-section';

const trustedCompaniesList = [
  { imageSrc: '/images/companies/aws.svg', label: 'AWS Image' },
  { imageSrc: '/images/companies/accenture.svg', label: 'Accenture Image' },
  { imageSrc: '/images/companies/bnp.svg', label: 'BNP Image' },
  { imageSrc: '/images/companies/shell.svg', label: 'Shell Image' },
  { imageSrc: '/images/companies/toyota.svg', label: 'Toyota Image' },
  { imageSrc: '/images/companies/cisco.svg', label: 'Cisco Image' },
  { imageSrc: '/images/companies/airbus.svg', label: 'Airbus Image' },
  { imageSrc: '/images/companies/vodafone.svg', label: 'Vodafone Image' },
] as const;

export default function GetPersonalizedDiscountPage({ page }: { page: Page }) {
  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.PersonalizedDiscount} />
      <PageLayout withoutVerticalPadding>
        <PersonalizedDiscountHero />

        <Box backgroundColor='accent.sky.100'>
          <ContentContainer>
            <Grid
              columnGap={{ base: '32px', xl: '64px' }}
              gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }}
              gridTemplateRows={{ base: '1fr', md: '1fr min-content' }}
              paddingY={{ base: '32px', md: '72px' }}
              templateAreas={{
                base: `"whyUdacitySection"
                "form" 
                "companiesSection"`,

                md: `"whyUdacitySection form"
                  "companiesSection form"`,
              }}
            >
              <GridItem area={'whyUdacitySection'}>
                <UdacityBenefitsSection />
              </GridItem>

              <GridItem area={'companiesSection'}>
                <Divider marginY='32px' />

                <Heading
                  as='p'
                  color='blue.400'
                  size='marketing-md'
                  textAlign={{
                    base: 'center',
                    md: 'left',
                  }}
                >
                  Trusted by the world&apos;s leading companies
                </Heading>

                <SimpleGrid marginTop='16px' minChildWidth='120px' spacing='16px'>
                  {trustedCompaniesList.map((trustedCompany) => (
                    <Box
                      key={trustedCompany.label}
                      alignItems='center'
                      display='flex'
                      height='56px'
                      justifyContent='center'
                    >
                      <Image
                        key={trustedCompany.label}
                        alt={trustedCompany.label}
                        height={56}
                        src={trustedCompany.imageSrc}
                        style={{ height: '56px', objectFit: 'scale-down' }}
                        width={120}
                      />
                    </Box>
                  ))}
                </SimpleGrid>
              </GridItem>

              <GridItem area={'form'} marginTop={{ base: '24px', lg: '0px' }}>
                <Card
                  margin='auto'
                  maxWidth={{ base: '568px', lg: 'unset' }}
                  padding={{
                    base: '24px',
                    xl: '40px',
                  }}
                >
                  <PersonalizedDiscountForm />
                </Card>
              </GridItem>
            </Grid>
          </ContentContainer>
        </Box>
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const page = await getPage('/get-personalized-discount');

  return {
    props: {
      page,
    },
  };
}
